/**
 * Phase I — Per-section WMS Mobile Dashboard.
 *
 * Literal clone of `pages/WMS/MobileDashboard.jsx` (Salem-untouched).
 * Mounted at `/sections/dispatch/:section/mobile`. Each tile routes
 * INSIDE the per-section URL space so all writes land in
 * `{slug}_*` collections via the strict X-Site-Id middleware (Phase G)
 * and dynamic collection resolver (Phase D infra).
 *
 * Salem-untouched contract: Salem's `MobileDashboard.jsx` is paired
 * with this file in `/app/SALEM_CLONE_DRIFT.md` (Phase F). Any future
 * change to Salem's mobile tiles must be back-ported here in the
 * same PR.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Package, Scan, Truck, BoxSelect, LogOut, User, Wifi, WifiOff,
  ClipboardCheck, Layers, Undo2, RotateCcw, AlertTriangle, ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/services/apiClient';

const SLUG_TO_DISPLAY = {
  color_combo: { name: 'Color Combo', tagline: 'WMS Mobile · Color Combo Section' },
  art_silk:    { name: 'Art Silk',    tagline: 'WMS Mobile · Art Silk Section' },
  kids:        { name: 'Kids',        tagline: 'WMS Mobile · Kids Section' },
};

const PerSectionMobileDashboard = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const meta = SLUG_TO_DISPLAY[slug];
  const basePath = `/sections/dispatch/${slug}`;
  const mobileBase = `${basePath}/mobile`;
  const apiBase  = `/per-section/${slug}`;

  const [user, setUser] = useState(null);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [stats, setStats] = useState({
    pendingSFGSlips: 0,
    pendingFGSlips: 0,
    pendingPutAwaySlips: 0,
    activeComboPlans: 0,
    pendingReturnExcess: 0,
    pendingLooseUSNs: 0,
  });

  // Fetch per-section badge counts. All endpoints are scoped to {slug}.
  const fetchStats = async () => {
    try {
      const [sfgRes, fgRes, comboPlans, looseRes] = await Promise.all([
        apiClient.get(`${apiBase}/wms/pick-slips?status=PENDING`).catch(() => ({ data: { total: 0 } })),
        apiClient.get(`${apiBase}/wms/fg/pick-slips?status=IMPORTED`).catch(() => ({ data: { total: 0 } })),
        apiClient.get(`${apiBase}/combo-v2-plans?status=ASSEMBLING`).catch(() => ({ data: { plans: [] } })),
        apiClient.get(`${apiBase}/combo-v2-reversal/loose-usns`).catch(() => ({ data: { total: 0 } })),
      ]);
      setStats({
        pendingSFGSlips: sfgRes.data?.total || 0,
        pendingFGSlips: fgRes.data?.total || 0,
        pendingPutAwaySlips: 0,
        activeComboPlans: (comboPlans.data?.plans || []).length || 0,
        pendingReturnExcess: 0,
        pendingLooseUSNs: looseRes.data?.total || 0,
      });
    } catch (error) {
      console.error('[per-section/mobile] fetchStats error:', error);
    }
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try { setUser(JSON.parse(userData)); } catch { /* ignore */ }
    }
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    if (slug) fetchStats();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('session_token');
    navigate('/login');
  };

  if (!meta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white p-6">
        <AlertTriangle className="h-10 w-10 text-amber-400 mb-3" />
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-400 mt-1">
          No catalog entry for slug <code className="font-mono">{slug}</code>.
        </p>
        <Button
          className="mt-4"
          onClick={() => navigate('/wms/mobile')}
          data-testid="back-to-salem-mobile-btn"
        >
          Back to WMS Mobile
        </Button>
      </div>
    );
  }

  // Per-section tile catalog. Order mirrors Salem's mobile dashboard so
  // muscle-memory is preserved, with combo-specific tiles appended.
  const modules = [
    {
      id: 'sfg-putaway',
      title: 'SFG Put-Away',
      subtitle: 'Store bundles in SFG',
      icon: Package,
      route: `${mobileBase}/sfg-put-away`,
      color: 'from-purple-500 to-purple-700',
      badge: null,
    },
    {
      id: 'sfg-picking',
      title: 'SFG Picking',
      subtitle: 'Pick for replenishment',
      icon: Truck,
      route: `${mobileBase}/sfg-pick`,
      color: 'from-cyan-500 to-cyan-700',
      badge: stats.pendingSFGSlips > 0 ? stats.pendingSFGSlips : null,
    },
    {
      id: 'gt-putaway',
      title: 'Internal Transfer',
      subtitle: 'Receive bundles from Goods Transfer',
      icon: ClipboardCheck,
      route: `${basePath}/internal-transfer`,
      color: 'from-emerald-500 to-emerald-700',
      badge: null,
    },
    {
      id: 'fg-putaway',
      title: 'FG Put-Away',
      subtitle: 'Put away combo USNs',
      icon: BoxSelect,
      route: `${mobileBase}/fg-put-away`,
      color: 'from-teal-500 to-teal-700',
      badge: null,
    },
    {
      id: 'fg-picking',
      title: 'FG Picking',
      subtitle: 'Pick for dispatch',
      icon: Scan,
      route: `${mobileBase}/fg-pick`,
      color: 'from-amber-500 to-amber-700',
      badge: stats.pendingFGSlips > 0 ? stats.pendingFGSlips : null,
    },
    {
      id: 'pp-station',
      title: 'PP Station',
      subtitle: 'Scan USNs to assemble combos',
      icon: Layers,
      route: `${basePath}/pp-station`,
      color: 'from-blue-500 to-blue-700',
      badge: stats.activeComboPlans > 0 ? stats.activeComboPlans : null,
    },
    {
      id: 'return-excess',
      title: 'Return Excess',
      subtitle: 'Wrap leftover USNs',
      icon: Undo2,
      route: `${basePath}/return-excess`,
      color: 'from-orange-500 to-orange-700',
      badge: null,
    },
    {
      id: 'combo-reversal',
      title: 'Combo Reversal',
      subtitle: 'Undo assembled combos',
      icon: RotateCcw,
      route: `${basePath}/combo-reversal`,
      color: 'from-rose-500 to-rose-700',
      badge: stats.pendingLooseUSNs > 0 ? stats.pendingLooseUSNs : null,
    },
  ];

  return (
    <div
      className="min-h-screen bg-gray-900 text-white flex flex-col"
      data-testid={`per-section-mobile-${slug}`}
    >
      {/* Header */}
      <header className="bg-gray-800 px-4 py-3 flex items-center justify-between safe-area-inset-top">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(basePath)}
            className="text-white hover:bg-white/10 -ml-1"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold">WMS Mobile</h1>
            <p className="text-xs text-gray-400">{meta.tagline}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {online ? (
            <Wifi className="w-5 h-5 text-green-400" data-testid="online-indicator" />
          ) : (
            <WifiOff className="w-5 h-5 text-red-400" data-testid="offline-indicator" />
          )}
        </div>
      </header>

      {/* User Info */}
      <div className="bg-gray-800/50 px-4 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-gray-400" />
          </div>
          <div>
            <p className="text-sm font-medium">{user?.name || 'Staff'}</p>
            <p className="text-xs text-gray-500">
              {(user?.role || 'Worker')} · {meta.name}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="text-gray-400 hover:text-white hover:bg-gray-700"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      {/* Main Content */}
      <main className="flex-1 p-4 space-y-4">
        <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
          Select Task
        </h2>

        <div className="grid grid-cols-1 gap-4">
          {modules.map((module) => {
            const Icon = module.icon;
            return (
              <button
                key={module.id}
                onClick={() => navigate(module.route)}
                className={`relative bg-gradient-to-r ${module.color} rounded-2xl p-6 text-left transition-transform active:scale-[0.98] shadow-lg min-h-[6rem]`}
                data-testid={`mobile-tile-${module.id}`}
              >
                {module.badge && (
                  <div
                    className="absolute top-3 right-3 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center text-sm font-bold animate-pulse"
                    data-testid={`badge-${module.id}`}
                  >
                    {module.badge}
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xl font-bold truncate">{module.title}</h3>
                    <p className="text-sm text-white/80">{module.subtitle}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 px-4 py-3 text-center safe-area-inset-bottom">
        <Button
          variant="ghost"
          onClick={() => navigate(basePath)}
          className="text-gray-400 hover:text-white text-sm"
          data-testid="switch-to-desktop-btn"
        >
          Switch to Desktop View
        </Button>
      </footer>
    </div>
  );
};

export default PerSectionMobileDashboard;
